import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Save, Plus, Eye, Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  FnsPreview,
  QuestionCard,
  QuestionErrors,
} from '@/features/forms-and-surveys/components'
import { SURVEY_TYPES } from '@/features/forms-and-surveys/constants'
import { useFnsMode } from '@/features/forms-and-surveys/hooks'
import {
  Survey,
  SurveyMutationInput,
  SurveyQuestionType,
  SurveyUiTemplate,
  SurveyType,
} from '@/features/forms-and-surveys/types'
import {
  getSurveyTypeColor,
  getTranslatedSurveyTypeLabel,
} from '@/features/forms-and-surveys/utils'
import {
  fnsFormSchema,
  FnsFormData,
} from '@/features/forms-and-surveys/zod-schema/fns-form.schema'

interface QuestionFormData {
  questionId?: string // For existing questions when updating
  index: number
  text: string
  type: SurveyQuestionType
  isRequired: boolean
  isActive?: boolean
  options?: string[]
  minValue?: number
  maxValue?: number
  actionLabel?: string
  actionUrl?: string
  uiTemplate?: SurveyUiTemplate
}

interface FnsActionFormProps {
  data?: Survey
  loading: boolean
  isEditMode: boolean
  onSubmit: (data: SurveyMutationInput) => Promise<void>
  onCancel?: () => void
}

export function FnsActionForm({
  data,
  loading,
  isEditMode,
  onSubmit,
  onCancel,
}: FnsActionFormProps) {
  const mode = useFnsMode()
  const { t } = useTranslation()
  const form = useForm<FnsFormData>({
    resolver: zodResolver(fnsFormSchema),
    mode: 'onChange', // Validate on change
    defaultValues: {
      title: data?.title || '',
      description: data?.description || '',
      type:
        data?.type && data.type.trim() !== ''
          ? data.type
          : 'CustomerSatisfactionScore',
      isActive: data?.isActive ?? true,
      isAnonymous: data?.isAnonymous ?? false,
      questions:
        data?.questions?.map((q, index) => ({
          questionId: q.id, // Include questionId for existing questions
          index: index + 1,
          text: q.text || '',
          type: q.type || 'Text',
          isRequired: q.isRequired ?? false,
          isActive: q.isActive ?? true,
          options: q.options || [],
          minValue: q.minValue || undefined,
          maxValue: q.maxValue || undefined,
          actionLabel: q.actionLabel || undefined,
          actionUrl: q.actionUrl || undefined,
          uiTemplate: q.uiTemplate || 'Default',
        })) ||
        // Add default text question for new surveys
        (data
          ? []
          : [
              {
                questionId: undefined,
                index: 1,
                text: '',
                type: 'Text' as SurveyQuestionType,
                isRequired: false,
                isActive: true,
                options: [],
                uiTemplate: 'Default' as SurveyUiTemplate,
              },
            ]),
    },
  })

  const [showPreview, setShowPreview] = useState(false)

  // Update form data when survey prop changes (for edit mode)
  useEffect(() => {
    if (data && isEditMode) {
      form.reset({
        title: data?.title || '',
        description: data?.description || '',
        type:
          data?.type && data.type.trim() !== ''
            ? data.type
            : 'CustomerSatisfactionScore',
        isActive: data?.isActive ?? true,
        isAnonymous: data?.isAnonymous ?? false,
        questions:
          data?.questions?.map((q, index) => ({
            questionId: q.id, // Include questionId for existing questions
            index: index + 1,
            text: q.text || '',
            type: q.type || 'Text',
            isRequired: q.isRequired ?? false,
            isActive: q.isActive ?? true,
            options: q.options || [],
            minValue: q.minValue || undefined,
            maxValue: q.maxValue || undefined,
            actionLabel: q.actionLabel || undefined,
            actionUrl: q.actionUrl || undefined,
            uiTemplate: q.uiTemplate || 'Default',
          })) || [],
      })
    } else if (!data && !isEditMode) {
      // Reset to default values for new survey creation
      form.reset({
        title: '',
        description: '',
        type: 'CustomerSatisfactionScore',
        isActive: true,
        isAnonymous: false,
        questions: [
          {
            questionId: undefined,
            index: 1,
            text: '',
            type: 'Text' as SurveyQuestionType,
            isRequired: false,
            isActive: true,
            options: [],
            uiTemplate: 'Default' as SurveyUiTemplate,
          },
        ],
      })
    }
  }, [data, isEditMode, form])

  // Question management functions
  const addQuestion = () => {
    const currentQuestions = form.getValues('questions')
    const newQuestion = {
      questionId: undefined, // New questions don't have questionId
      index: currentQuestions.length + 1,
      text: '',
      type: 'Text' as SurveyQuestionType,
      isRequired: false,
      isActive: true,
      options: [],
      uiTemplate: 'Default' as SurveyUiTemplate,
    }
    form.setValue('questions', [...currentQuestions, newQuestion])
    // Trigger validation after adding question
    form.trigger('questions')
  }

  const updateQuestion = (
    index: number,
    updates: Partial<QuestionFormData>
  ) => {
    const currentQuestions = form.getValues('questions')
    const updatedQuestions = currentQuestions.map((q, i) =>
      i === index ? { ...q, ...updates } : q
    )
    form.setValue('questions', updatedQuestions)
    // Trigger validation after updating question
    form.trigger('questions')
  }

  const deleteQuestion = (index: number) => {
    const currentQuestions = form.getValues('questions')
    const updatedQuestions = currentQuestions
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, index: i + 1 }))
    form.setValue('questions', updatedQuestions)
    // Trigger validation after deleting question
    form.trigger('questions')
  }

  const moveQuestionUp = (index: number) => {
    if (index <= 0) return

    const currentQuestions = form.getValues('questions')
    const questions = [...currentQuestions]
    const temp = questions[index]
    questions[index] = questions[index - 1]
    questions[index - 1] = temp

    // Update indices
    const updatedQuestions = questions.map((q, i) => ({ ...q, index: i + 1 }))
    form.setValue('questions', updatedQuestions)
    // Trigger validation after moving question
    form.trigger('questions')
  }

  const moveQuestionDown = (index: number) => {
    const currentQuestions = form.getValues('questions')
    if (index >= currentQuestions.length - 1) return

    const questions = [...currentQuestions]
    const temp = questions[index]
    questions[index] = questions[index + 1]
    questions[index + 1] = temp

    // Update indices
    const updatedQuestions = questions.map((q, i) => ({ ...q, index: i + 1 }))
    form.setValue('questions', updatedQuestions)
    // Trigger validation after moving question
    form.trigger('questions')
  }

  const handleSubmit = async (data: FnsFormData) => {
    try {
      // Convert form data to mutation input format
      const mutationData: SurveyMutationInput = {
        title: data.title,
        description: data.description,
        type: data.type as SurveyType,
        isActive: data.isActive,
        isAnonymous: data.isAnonymous,
        questions: data.questions.map((q) => ({
          ...q,
          questionId: q.questionId, // Include questionId for existing questions
          uiTemplate: q.uiTemplate || ('Default' as SurveyUiTemplate),
        })),
      }
      await onSubmit(mutationData)
    } catch (_error) {
      // Error handled by parent component
    }
  }

  // Create a survey object for preview
  const previewSurvey: Survey = {
    id: data?.id || 'preview',
    title: form.getValues('title'),
    description: form.getValues('description') || '',
    type: form.getValues('type'),
    isActive: form.getValues('isActive') ?? true,
    isAnonymous: form.getValues('isAnonymous') ?? false,
    questions: form.getValues('questions').map((q) => ({
      id: `preview-${q.index}`,
      text: q.text,
      type: q.type,
      index: q.index - 1, // Convert back to 0-based for display
      isRequired: q.isRequired,
      isActive: q.isActive ?? true,
      options: q.options,
      minValue: q.minValue,
      maxValue: q.maxValue,
      actionLabel: q.actionLabel,
      actionUrl: q.actionUrl,
      uiTemplate: q.uiTemplate,
    })),
    tenantId: data?.tenantId || '',
    createdAt: data?.createdAt,
    updatedAt: data?.updatedAt,
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <Button variant='ghost' size='sm' asChild className='-ml-2'>
              <Link to={mode === 'survey' ? '/surveys' : '/forms'}>
                <ArrowLeft className='h-4 w-4' />
                {t('common.back')} {t(`navigation.${mode}s`)}
              </Link>
            </Button>

            <div className='flex items-center gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setShowPreview(true)}
                disabled={form.watch('questions').length === 0}
              >
                <Eye className='h-4 w-4' />
                {t('common.view')}
              </Button>
              <Button
                type='button'
                variant='outline'
                onClick={onCancel}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button type='submit' disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    {isEditMode ? t('common.updating') : t('common.creating')}
                  </>
                ) : (
                  <>
                    <Save className='h-4 w-4' />
                    {isEditMode
                      ? t(
                          `${mode}s.edit${mode.charAt(0).toUpperCase() + mode.slice(1)}`
                        )
                      : t(
                          `${mode}s.create${mode.charAt(0).toUpperCase() + mode.slice(1)}`
                        )}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('surveys.basicInformation')}</CardTitle>
              <CardDescription>
                {t(
                  `surveys.createNew${mode.charAt(0).toUpperCase() + mode.slice(1)}Description`
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <FormField
                control={form.control}
                name='title'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.name')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t(`surveys.titlePlaceholder`)}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t(`surveys.descriptionPlaceholder`)}
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                {mode === 'survey' ? (
                  <FormField
                    control={form.control}
                    name='type'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('surveys.surveyType')}</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || 'CustomerSatisfactionScore'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SURVEY_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                <Badge className={getSurveyTypeColor(type)}>
                                  {getTranslatedSurveyTypeLabel(t, type)}
                                </Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}

                <FormField
                  control={form.control}
                  name='isActive'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                      <div className='space-y-0.5'>
                        <FormLabel className='text-base'>
                          {t('common.active')}
                        </FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='isAnonymous'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                      <div className='space-y-0.5'>
                        <FormLabel className='text-base'>
                          {t('surveys.anonymous')}
                        </FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div>
                  <CardTitle>{t('surveys.questions')}</CardTitle>
                  <CardDescription>
                    {t('surveys.addAndManageQuestions')}
                  </CardDescription>
                </div>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={addQuestion}
                >
                  <Plus className='h-4 w-4' />
                  {t('surveys.addQuestion')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              {form.watch('questions').length === 0 ? (
                <div className='py-8 text-center text-muted-foreground'>
                  <p>{t('surveys.noQuestionsAddedYet')}</p>
                </div>
              ) : (
                <>
                  {/* Questions array error */}
                  {form.formState.errors.questions &&
                    !Array.isArray(form.formState.errors.questions) && (
                      <div className='rounded-md border border-red-200 bg-red-50 p-3'>
                        <p className='text-sm text-red-600'>
                          {form.formState.errors.questions.message ||
                            t('surveys.questionsValidationFailed')}
                        </p>
                      </div>
                    )}

                  {form.watch('questions').map((question, index) => {
                    // Extract errors for this specific question
                    const questionErrors = form.formState.errors.questions?.[
                      index
                    ] as QuestionErrors

                    return (
                      <div key={index} className='space-y-2'>
                        <QuestionCard
                          question={question as QuestionFormData}
                          index={index}
                          errors={questionErrors}
                          onUpdate={updateQuestion}
                          onDelete={deleteQuestion}
                          onMoveUp={moveQuestionUp}
                          onMoveDown={moveQuestionDown}
                          canMoveUp={index > 0}
                          canMoveDown={
                            index < form.watch('questions').length - 1
                          }
                        />
                      </div>
                    )
                  })}
                </>
              )}
            </CardContent>
          </Card>
        </form>
      </Form>

      {/* Preview Modal */}
      <FnsPreview
        data={previewSurvey}
        open={showPreview}
        onOpenChange={setShowPreview}
      />
    </>
  )
}
